import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { programContent } from '../../data/program';
import ProgramNav from '../../components/program/ProgramNav';
import AnimatedSection from '../../components/common/AnimatedSection';
import { 
  Building2, GraduationCap, Lightbulb, Handshake, BookOpen,
  Globe, Users, Shield, MapPin, Mail, ArrowRight
} from 'lucide-react';

const HouseOfAlliancePage = () => {
  const { language } = useLanguage();
  const content = programContent[language]?.house || programContent.en.house;

  const pillarIcons = [Building2, GraduationCap, Lightbulb, Handshake, BookOpen];
  const governanceIcons = [Globe, Users, Shield];

  return (
    <div className="pt-24">
      <ProgramNav />
      
      {/* Hero */}
      <section className="section bg-[#5B2D8E]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
                <MapPin className="w-4 h-4 text-[#F5A623]" />
                <span className="text-sm font-medium text-[#F5A623]">{content.hero.location}</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display text-white mb-4">{content.hero.title}</h1>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="text-xl text-white/80" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.hero.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <span className="caption mb-4 block">{content.intro.title}</span>
              <div className="divider mb-8" />
              <p className="text-xl text-[#2D2D2D] leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.intro.content}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Functions - 5 Pillars */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Operational Framework</span>
            <h2 className="heading-1 mb-4">{content.pillars.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.pillars.items.map((pillar, index) => {
                const Icon = pillarIcons[index];
                return (
                  <AnimatedSection key={index} delay={index * 0.1}>
                    <div className={`bg-white rounded-xl p-6 border border-[#E8E0D4] h-full ${
                      index === 0 ? 'lg:col-span-2' : ''
                    }`}>
                      <div className="w-12 h-12 bg-[#5B2D8E]/10 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-[#5B2D8E]" />
                      </div>
                      <h3 className="font-semibold text-[#2D2D2D] mb-3">{pillar.title}</h3>
                      <p className="text-[#6B6B6B]">{pillar.description}</p>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Governance Framework */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Leadership Structure</span>
            <h2 className="heading-1 mb-4">{content.governance.title}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-4xl mx-auto space-y-6">
            {content.governance.bodies.map((body, index) => {
              const Icon = governanceIcons[index];
              return (
                <AnimatedSection key={index} delay={index * 0.15}>
                  <div className="bg-[#F5F0E8] rounded-xl p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-[#5B2D8E] text-white' :
                      index === 1 ? 'bg-[#F5A623] text-white' :
                      'bg-[#0D7377] text-white'
                    }`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-[#2D2D2D] mb-2">{body.name}</h3>
                      <p className="text-[#6B6B6B]">{body.description}</p>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Johannesburg */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="bg-white rounded-2xl p-8 lg:p-10 border border-[#E8E0D4]">
                <div className="flex items-start gap-4 mb-6">
                  <MapPin className="w-8 h-8 text-[#5B2D8E] flex-shrink-0" />
                  <h2 className="heading-2">{content.whyJohannesburg.title}</h2>
                </div>
                <ul className="space-y-4">
                  {content.whyJohannesburg.reasons.map((reason, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <span className="w-6 h-6 bg-[#F5A623]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-2 h-2 bg-[#F5A623] rounded-full" />
                      </span>
                      <span className="text-[#2D2D2D]">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Building2 className="w-12 h-12 mx-auto mb-6 text-[#5B2D8E]" />
              <h2 className="heading-2 mb-4">{content.cta.title}</h2>
              <p className="body-large mb-6">{content.cta.description}</p>
              <p className="text-[#6B6B6B] mb-8">{content.cta.contact}</p>
              <a href="mailto:contact@ada-alliance.org" className="btn-primary">
                <Mail className="w-4 h-4" />
                Contact Us
              </a>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HouseOfAlliancePage;
